/**
 * Pure helpers for chunk-load failure recovery, kept JSX-free so they can be
 * unit-tested under the repo's Jest/Babel setup (preset-env only). The React
 * boundary that uses them lives in ./ChunkErrorBoundary.js.
 */

export const RELOAD_KEY = 'ti_chunk_reload_at';
export const RELOAD_WINDOW_MS = 10000;

const CHUNK_ERROR_PATTERNS = [
  /Loading (CSS )?chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
  /Unexpected token '<'/
];

/**
 * True when an error looks like a code-split chunk that failed to load/parse.
 * @param {Error|null|undefined} error
 * @returns {boolean}
 */
export function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const message = typeof error.message === 'string' ? error.message : '';
  return CHUNK_ERROR_PATTERNS.some(re => re.test(message));
}

/**
 * Decide whether to reload now, given the last-reload timestamp. Returns false
 * when a reload already happened within windowMs (loop guard).
 * @param {number} now
 * @param {number} lastReload
 * @param {number} windowMs
 * @returns {boolean}
 */
export function shouldReload(now, lastReload, windowMs = RELOAD_WINDOW_MS) {
  if (!lastReload) return true;
  return now - lastReload >= windowMs;
}

/** Read the last-reload timestamp from sessionStorage (0 when unavailable). */
export function readLastReload() {
  try {
    return parseInt(window.sessionStorage.getItem(RELOAD_KEY), 10) || 0;
  } catch (e) {
    return 0;
  }
}

/** Persist the last-reload timestamp; no-op when sessionStorage is unavailable. */
export function writeLastReload(now) {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch (e) {
    /* sessionStorage disabled (private mode) — ignore */
  }
}
